/**
 * ctaRegistry.js — el censo de los CTAs propios de la app.
 * =======================================================
 *
 * Un CTA interno es un botón de captación o de upgrade que pintamos NOSOTROS
 * dentro del producto (landing, precios, modales de upsell, bloqueos de Pro…).
 * No confundir con dos cosas que también se llaman «CTA» en este repo:
 *
 *   - Los enlaces del «Todo en 1» (`products.todo_en_1_config.links[]`), que
 *     son del NEGOCIO y los pulsa su visitante. Tienen su propio tracking en
 *     `POST /api/v1/products/<id>/click/`.
 *   - Los `cta_links` de campaña (`/go/<id>/`), que crea un admin a mano para
 *     pegarlos en un email o un anuncio.
 *
 * Tres reglas que hay que respetar al tocar este fichero:
 *
 * REGLA 1. Un `<CtaLink>` con un `cta` que no esté aquí NO manda nada y avisa
 *          por consola en desarrollo. El test `__tests__/ctaRegistry.test.js`
 *          lo hace fallar en CI, en las dos direcciones: ni ids fantasma en el
 *          código, ni entradas muertas en esta lista.
 *
 * REGLA 2. Cambiar un `id` NO es renombrar: es crear otro CTA. `cta_events` va
 *          keyeado por el id y no se migra nada, así que el histórico se parte
 *          en dos filas y la vieja aparece como «retirado». Si de verdad hace
 *          falta, hazlo a sabiendas — el test de paridad te obligará a tocar
 *          también el espejo Python, que es donde queríamos que te pararas.
 *
 * REGLA 3. Este fichero tiene un espejo en `QR/qr_app/cta_registry.py`, que es
 *          la allowlist del endpoint de ingesta. Si añades aquí y no allí, el
 *          backend descarta los clicks en silencio. El test compara los dos
 *          conjuntos de ids. Mismo patrón que `includedDeviceStates.js`.
 */

/**
 * Las superficies, cerradas a propósito: son la agrupación de la tabla del
 * panel y cada una tiene su clave i18n `adminCtaLinks.surface.<value>`.
 * Sin acentos y en ASCII pelado porque viajan por la API y se filtran por
 * igualdad (mismo motivo que los slugs de `includedDeviceStates.js`).
 */
export const CTA_SURFACES = [
  { value: 'landing', labelKey: 'adminCtaLinks.surface.landing' },
  // `funciones`, `casos` y `dispositivos` se fueron con las páginas de la V2
  // que las usaban. Los eventos que ya estaban grabados NO se pierden: el panel
  // los agrupa en «retirados» al no encontrar su id en el registro
  // (`looks_internal` en `qr_app/cta_registry.py`).
  //
  // `precios` vuelve —la V2 tenía una `pricing` que se retiró con ella— porque
  // vuelve a haber página de precios, ahora en los cinco idiomas. El VALOR es
  // distinto a propósito: los `pricing.*` viejos siguen en «retirados» y no se
  // mezclan con los nuevos, que miden otra página.
  { value: 'precios', labelKey: 'adminCtaLinks.surface.precios' },
  { value: 'multiubicaciones', labelKey: 'adminCtaLinks.surface.multiubicaciones' },
  { value: 'producto', labelKey: 'adminCtaLinks.surface.producto' },
  { value: 'sectores', labelKey: 'adminCtaLinks.surface.sectores' },
  { value: 'redirect', labelKey: 'adminCtaLinks.surface.redirect' },
  { value: 'modal', labelKey: 'adminCtaLinks.surface.modal' },
  { value: 'app', labelKey: 'adminCtaLinks.surface.app' },
  { value: 'onboarding', labelKey: 'adminCtaLinks.surface.onboarding' },
  { value: 'retention', labelKey: 'adminCtaLinks.surface.retention' },
  { value: 'google', labelKey: 'adminCtaLinks.surface.google' },
  // Los anuncios del Plano Google Maps pt dentro de la app (diseño V5): la
  // tarjeta de Dispositivos, la franja de cada página y la ventana del plan.
  // Superficie propia para que salgan juntos y aparte en Admin → CTAs.
  { value: 'ofertapt', labelKey: 'adminCtaLinks.surface.ofertapt' },
];

/**
 * Qué persigue el CTA. Decide qué columnas tienen sentido en la tabla:
 *
 *   signup    → cuenta altas y pagos.
 *   upgrade   → cuenta pagos (quien lo pulsa YA tiene cuenta).
 *   nav       → solo clicks; lleva a otra parte del sitio, no convierte.
 *   store     → solo clicks; se va a la tienda de hardware, fuera de nuestro dominio.
 *   sales     → solo clicks; abre el chat con ventas.
 *   retention → solo clicks. Es un botón de CANCELAR: contarle «conversión»
 *               sería leer el número al revés. Va en su propio bloque.
 *   connect   → cuenta CONEXIONES de Google, no altas ni pagos. Quien lo pulsa
 *               ya tiene cuenta y puede llevar meses pagando: lo que mide es si
 *               el botón consiguió que Google devolviera una ficha conectada.
 *               El evento lo emite el servidor en el callback del OAuth, no el
 *               navegador — ver `cta_registry.py` y decisiones §123.
 */
export const CTA_GOALS = ['signup', 'upgrade', 'nav', 'store', 'sales', 'retention', 'connect'];

/** Metas para las que las columnas de altas/pagos significan algo. */
export const CONVERTING_GOALS = ['signup', 'upgrade'];

export const CTA_REGISTRY = [
  // ── Landing pública ────────────────────────────────────────────────────
  { id: 'landing.hero.precios', surface: 'landing', page: 'Landing', goal: 'nav',
    name: 'Hero · Ver precios', destination: '#precios' },

  // Auditor SEO: el muro borroso que hay bajo el informe del visitante. Es el
  // único CTA del componente — el botón de «analizar» no se declara porque no
  // empuja a registrarse ni a pagar, y meterlo ensuciaría el censo.
  { id: 'landing.auditor.desbloquear', surface: 'landing', page: 'AuditorSeo', goal: 'signup',
    name: 'Auditor SEO · Desbloquear el plan de mejora', destination: '/register' },

  // La barra de navegación es la MISMA en Landing, Precios, Funciones, Casos y
  // Dispositivos, así que sus tres CTAs son uno cada uno, no cinco. El desglose
  // por página sale de `path`, que se guarda con cada evento.
  { id: 'landing.nav.registro', surface: 'landing', page: 'LandingNav', goal: 'signup',
    name: 'Cabecera · Crear cuenta', destination: '/register' },

  // Cabecera del rediseño V3. Ids propios y no reutilizar los de `landing.nav.*`
  // porque los `cta_events` van keyeados por id y no se migra nada: compartirlo
  // mezclaría en una sola fila los clicks de dos cabeceras distintas y haría
  // ilegible la comparación entre la V2 y la V3 mientras conviven.
  //
  // Son dos y no tres: el diseño V3 no tiene botón de «Registrarse» suelto, solo
  // «Iniciar sesión» y «Probar 7 días gratis». Los ocho enlaces de los
  // desplegables NO se declaran a propósito — son navegación entre páginas del
  // sitio, no empujan a registrarse ni a pagar, y meterlos ensuciaría el censo
  // (mismo criterio que el botón de «analizar» del auditor).
  { id: 'landing.v3.nav.login', surface: 'landing', page: 'LandingNavV3', goal: 'nav',
    name: 'Cabecera V3 · Entrar', destination: '/login' },
  { id: 'landing.v3.nav.trial', surface: 'landing', page: 'LandingNavV3', goal: 'signup',
    name: 'Cabecera V3 · Probar 7 días gratis', destination: '/register' },
  { id: 'landing.v3.planes.gratis', surface: 'landing', page: 'PricingTableV3', goal: 'signup',
    name: 'Planes V3 · Empezar gratis', destination: '/register' },
  { id: 'landing.v3.planes.trial', surface: 'landing', page: 'PricingTableV3', goal: 'signup',
    name: 'Planes V3 · Empezar prueba', destination: '/register' },
  { id: 'landing.v3.planes.ventas', surface: 'landing', page: 'PricingTableV3', goal: 'sales',
    name: 'Planes V3 · Contactar con ventas', destination: 'chat' },
  // El enlace «¿Más de 30 locales? Mira Enterprise» que sale en la columna
  // Business al llegar al tope del stepper.
  { id: 'landing.v3.planes.enterprise-mas-30', surface: 'landing', page: 'PricingTableV3', goal: 'sales',
    name: 'Planes V3 · Más de 30 locales → Enterprise', destination: 'chat' },
  { id: 'landing.v3.hero.trial', surface: 'landing', page: 'HomeV3', goal: 'signup',
    name: 'Hero V3 · Probar 7 días gratis', destination: '/register' },
  { id: 'landing.v3.hero.como', surface: 'landing', page: 'HomeV3', goal: 'nav',
    name: 'Hero V3 · Ver cómo funciona', destination: '#como-funciona' },
  { id: 'landing.v3.cierre.trial', surface: 'landing', page: 'CtaFinalOscuro', goal: 'signup',
    name: 'Cierre V3 · Probar 7 días gratis', destination: '/register' },
  { id: 'landing.v3.cierre.ventas', surface: 'landing', page: 'CtaFinalOscuro', goal: 'sales',
    name: 'Cierre V3 · Contactar con ventas', destination: 'chat' },

  // ── Precios (V3) ────────────────────────────────────────────────────────
  // La página `/pricing` monta el MISMO `PricingTableV3` que la home, así que
  // los tres botones de la tabla (`landing.v3.planes.*`) se comparten entre las
  // dos y eso está bien: el desglose por pantalla de Admin → CTAs los separa
  // por el `path` del evento.
  //
  // Los del cierre sí son propios. Si se compartieran con los de la home no
  // habría forma de saber si un alta salió de una o de otra, que es
  // exactamente la pregunta que esta página existe para responder.
  { id: 'precios.cierre.trial', surface: 'precios', page: 'PreciosV3', goal: 'signup',
    name: 'Precios · Cierre · Probar gratis', destination: '/register' },
  { id: 'precios.cierre.ventas', surface: 'precios', page: 'PreciosV3', goal: 'sales',
    name: 'Precios · Cierre · Contactar con ventas', destination: 'chat' },
  // Con sesión, `/pricing` no enseña la tabla sino el checkout
  // (`PreciosSegunSesion`): es el destino de los CTAs de la app que mandan a
  // contratar (`SentimentLockedCard`, `SeatActionPanel`, `ConnectionList`).
  { id: 'precios.sesion.empezar-trial', surface: 'precios', page: 'PreciosSegunSesion', goal: 'upgrade',
    name: 'Precios · Con sesión · Empezar', destination: 'stripe' },

  // ── Sobre nosotros (V3) ────────────────────────────────────────────────
  // Página institucional enlazada desde el pie. Ids propios por lo mismo que
  // los de Precios: saber si un alta salió de quien quiso conocernos antes.
  { id: 'sobre.hero.trial', surface: 'landing', page: 'SobreNosotrosV3', goal: 'signup',
    name: 'Sobre nosotros · Hero · Probar gratis', destination: '/register' },
  { id: 'sobre.cierre.trial', surface: 'landing', page: 'SobreNosotrosV3', goal: 'signup',
    name: 'Sobre nosotros · Cierre · Probar gratis', destination: '/register' },
  { id: 'sobre.cierre.ventas', surface: 'landing', page: 'SobreNosotrosV3', goal: 'sales',
    name: 'Sobre nosotros · Cierre · Contactar con ventas', destination: 'chat' },

  // ── Producto (V3) ──────────────────────────────────────────────────────
  // Cuatro páginas con los mismos cuatro botones. El id lleva la página DENTRO
  // (`producto.visibilidad.*`, `producto.widget.*`) y no se comparte: la
  // pregunta que hay que poder responder es cuál de las cuatro trae altas, y
  // con un id común para el hero las cuatro se mezclarían en un solo embudo.
  { id: 'producto.visibilidad.hero.trial', surface: 'producto', page: 'VisibilidadSeoV3',
    goal: 'signup', name: 'Visibilidad SEO · Hero · Probar gratis', destination: '/register' },
  { id: 'producto.visibilidad.hero.ver', surface: 'producto', page: 'VisibilidadSeoV3',
    goal: 'nav', name: 'Visibilidad SEO · Hero · Ver el escáner', destination: '#escaneo' },
  { id: 'producto.visibilidad.cierre.trial', surface: 'producto', page: 'VisibilidadSeoV3',
    goal: 'signup', name: 'Visibilidad SEO · Cierre · Probar gratis', destination: '/register' },
  { id: 'producto.visibilidad.cierre.ventas', surface: 'producto', page: 'VisibilidadSeoV3',
    goal: 'sales', name: 'Visibilidad SEO · Cierre · Contactar con ventas', destination: 'chat' },

  { id: 'producto.automatizaciones.hero.trial', surface: 'producto', page: 'AutomatizacionesIaV3',
    goal: 'signup', name: 'Automatizaciones · Hero · Probar gratis', destination: '/register' },
  { id: 'producto.automatizaciones.hero.ver', surface: 'producto', page: 'AutomatizacionesIaV3',
    goal: 'nav', name: 'Automatizaciones · Hero · Ver ejemplos', destination: '#ejemplos' },
  { id: 'producto.automatizaciones.cierre.trial', surface: 'producto', page: 'AutomatizacionesIaV3',
    goal: 'signup', name: 'Automatizaciones · Cierre · Probar gratis', destination: '/register' },
  { id: 'producto.automatizaciones.cierre.ventas', surface: 'producto', page: 'AutomatizacionesIaV3',
    goal: 'sales', name: 'Automatizaciones · Cierre · Contactar con ventas', destination: 'chat' },

  { id: 'producto.publicaciones.hero.trial', surface: 'producto', page: 'PublicacionesIaV3',
    goal: 'signup', name: 'Publicaciones IA · Hero · Probar gratis', destination: '/register' },
  { id: 'producto.publicaciones.hero.ver', surface: 'producto', page: 'PublicacionesIaV3',
    goal: 'nav', name: 'Publicaciones IA · Hero · Ver un ejemplo', destination: '#ejemplo' },
  { id: 'producto.publicaciones.cierre.trial', surface: 'producto', page: 'PublicacionesIaV3',
    goal: 'signup', name: 'Publicaciones IA · Cierre · Probar gratis', destination: '/register' },
  { id: 'producto.publicaciones.cierre.ventas', surface: 'producto', page: 'PublicacionesIaV3',
    goal: 'sales', name: 'Publicaciones IA · Cierre · Contactar con ventas', destination: 'chat' },

  { id: 'producto.widget.hero.trial', surface: 'producto', page: 'WidgetResenasV3',
    goal: 'signup', name: 'Widget de reseñas · Hero · Probar gratis', destination: '/register' },
  { id: 'producto.widget.hero.ver', surface: 'producto', page: 'WidgetResenasV3',
    goal: 'nav', name: 'Widget de reseñas · Hero · Ver los formatos', destination: '#formatos' },
  { id: 'producto.widget.cierre.trial', surface: 'producto', page: 'WidgetResenasV3',
    goal: 'signup', name: 'Widget de reseñas · Cierre · Probar gratis', destination: '/register' },
  { id: 'producto.widget.cierre.ventas', surface: 'producto', page: 'WidgetResenasV3',
    goal: 'sales', name: 'Widget de reseñas · Cierre · Contactar con ventas', destination: 'chat' },

  // ── Soluciones / sectores (V3) ────────────────────────────────────
  // Las cuatro las pinta el MISMO componente (`SectorV3`), pero cada una
  // tiene sus ids: qué sector convierte es justo lo que hay que poder mirar,
  // y un id compartido fundiría los cuatro embudos en uno.
  { id: 'sector.restauracion.hero.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Restauración · Hero · Probar gratis', destination: '/register' },
  { id: 'sector.restauracion.hero.multi', surface: 'sectores', page: 'SectorV3',
    goal: 'nav', name: 'Restauración · Hero · Ver varios locales', destination: '/multiubicaciones' },
  { id: 'sector.restauracion.cierre.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Restauración · Cierre · Probar gratis', destination: '/register' },
  { id: 'sector.restauracion.cierre.ventas', surface: 'sectores', page: 'SectorV3',
    goal: 'sales', name: 'Restauración · Cierre · Contactar con ventas', destination: 'chat' },

  { id: 'sector.clinicas.hero.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Clínicas · Hero · Probar gratis', destination: '/register' },
  { id: 'sector.clinicas.hero.multi', surface: 'sectores', page: 'SectorV3',
    goal: 'nav', name: 'Clínicas · Hero · Ver varias sedes', destination: '/multiubicaciones' },
  { id: 'sector.clinicas.cierre.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Clínicas · Cierre · Probar gratis', destination: '/register' },
  { id: 'sector.clinicas.cierre.ventas', surface: 'sectores', page: 'SectorV3',
    goal: 'sales', name: 'Clínicas · Cierre · Contactar con ventas', destination: 'chat' },

  { id: 'sector.automotriz.hero.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Automotriz · Hero · Probar gratis', destination: '/register' },
  { id: 'sector.automotriz.hero.multi', surface: 'sectores', page: 'SectorV3',
    goal: 'nav', name: 'Automotriz · Hero · Ver varias sedes', destination: '/multiubicaciones' },
  { id: 'sector.automotriz.cierre.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Automotriz · Cierre · Probar gratis', destination: '/register' },
  { id: 'sector.automotriz.cierre.ventas', surface: 'sectores', page: 'SectorV3',
    goal: 'sales', name: 'Automotriz · Cierre · Contactar con ventas', destination: 'chat' },

  { id: 'sector.hoteles.hero.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Hoteles · Hero · Probar gratis', destination: '/register' },
  { id: 'sector.hoteles.hero.multi', surface: 'sectores', page: 'SectorV3',
    goal: 'nav', name: 'Hoteles · Hero · Ver varios establecimientos', destination: '/multiubicaciones' },
  { id: 'sector.hoteles.cierre.trial', surface: 'sectores', page: 'SectorV3',
    goal: 'signup', name: 'Hoteles · Cierre · Probar gratis', destination: '/register' },
  { id: 'sector.hoteles.cierre.ventas', surface: 'sectores', page: 'SectorV3',
    goal: 'sales', name: 'Hoteles · Cierre · Contactar con ventas', destination: 'chat' },

  // ── Multiubicaciones (V3) ──────────────────────────────────────────────
  // Meta `signup` y no `sales`: quien rellena esto deja sus datos y entra en el
  // embudo comercial, que es lo mismo que hace un registro. `sales` es para los
  // botones que abren el chat y no dejan rastro.
  // Meta `nav` y no `signup`: este botón NO lleva al registro, baja al
  // formulario de la misma página. Quien convierte es el de abajo.
  { id: 'multiubicaciones.hero.info', surface: 'multiubicaciones', page: 'MultiubicacionesV3',
    goal: 'nav', name: 'Multiubicaciones · Hero · Solicitar información', destination: '#hablemos' },
  { id: 'multiubicaciones.form.enviar', surface: 'multiubicaciones', page: 'ContactoForm',
    goal: 'signup', name: 'Multiubicaciones · Solicitar información', destination: 'lead' },

  // ── Precios ────────────────────────────────────────────────────────────

  // ── Funciones ──────────────────────────────────────────────────────────

  // ── Casos de uso ───────────────────────────────────────────────────────

  // ── Dispositivos (página pública de hardware) ──────────────────────────

  // ── Pantalla post-escaneo de un dispositivo sin dueño ───────────────────
  { id: 'redirect.nuevo.crear-cuenta', surface: 'redirect', page: 'Redirect', goal: 'signup',
    name: 'Escaneo · Crear cuenta gratis', destination: '/register' },

  // ── Modales y tarjetas de upsell ───────────────────────────────────────
  { id: 'modal.upsell.abrir', surface: 'modal', page: 'BusinessUpsellCta', goal: 'upgrade',
    name: 'Cápsula de cabecera · Pasar a Business', destination: 'PricingModal' },
  { id: 'modal.precios.empezar-trial', surface: 'modal', page: 'PricingModal', goal: 'upgrade',
    name: 'Modal de precios · Empezar', destination: 'stripe' },
  // Plano Google Maps pt (billing/oferta_pt.py). Los anuncios de dentro de la
  // app van en su propia superficie, `ofertapt`, al final.
  // La landing del plan (`/plano-google-maps` y `/oferta/<token>`). Cinco
  // «Começar o meu plano», uno por sitio, para saber cuál vende: cada uno pasa
  // su id al checkout. Con el enlace del correo el alta se la lleva el link de
  // campaña `email-oferta-pt-entrar` y el botón queda como vía (§136).
  { id: 'landing.oferta-pt.comprar-hero', surface: 'landing', page: 'OfertaPtV4', goal: 'upgrade',
    name: 'Plano Google Maps pt · Hero', destination: 'stripe' },
  { id: 'landing.oferta-pt.comprar-nav', surface: 'landing', page: 'OfertaPtV4', goal: 'upgrade',
    name: 'Plano Google Maps pt · Barra superior (ordenador)', destination: 'stripe' },
  { id: 'landing.oferta-pt.comprar-preco', surface: 'landing', page: 'OfertaPtV4', goal: 'upgrade',
    name: 'Plano Google Maps pt · Tarjeta de precio', destination: 'stripe' },
  { id: 'landing.oferta-pt.comprar-final', surface: 'landing', page: 'OfertaPtV4', goal: 'upgrade',
    name: 'Plano Google Maps pt · Cierre de la página', destination: 'stripe' },
  { id: 'landing.oferta-pt.comprar-barra', surface: 'landing', page: 'OfertaPtV4', goal: 'upgrade',
    name: 'Plano Google Maps pt · Barra fija inferior', destination: 'stripe' },
  // El «Analisar» del diagnóstico de quien no tiene la ficha conectada: mide
  // cuántos de los que ven las tarjetas vacías lanzan su escaneo.
  { id: 'landing.oferta-pt.analisar', surface: 'landing', page: 'OfertaPtV4', goal: 'nav',
    name: 'Plano Google Maps pt · Analisar a minha ficha (sem GMB)', destination: 'diagnostico' },
  { id: 'landing.oferta-pt.whatsapp', surface: 'landing', page: 'OfertaPtV4', goal: 'sales',
    name: 'Plano Google Maps pt · Fala com o Afaq (WhatsApp)', destination: 'whatsapp' },
  { id: 'modal.cambiar-plan.empezar-trial', surface: 'modal', page: 'UpgradePlanModal', goal: 'upgrade',
    name: 'Cambiar de plan · Empezar', destination: 'stripe' },
  { id: 'modal.business-todos.asignar', surface: 'modal', page: 'BusinessForAllCard', goal: 'upgrade',
    name: 'Business para todos · Asignar locales', destination: 'asignar' },
  { id: 'modal.business-todos.comprar', surface: 'modal', page: 'BusinessForAllCard', goal: 'upgrade',
    name: 'Business para todos · Comprar locales', destination: 'stripe' },
  { id: 'modal.business-todos.upgrade', surface: 'modal', page: 'BusinessForAllCard', goal: 'upgrade',
    name: 'Business para todos · Pasar a Business', destination: 'stripe' },
  { id: 'modal.business-todos.soporte', surface: 'modal', page: 'BusinessForAllCard', goal: 'sales',
    name: 'Business para todos · Hablar con soporte', destination: 'chat' },

  // ── Dentro de la app ───────────────────────────────────────────────────
  { id: 'app.ajustes.empezar-trial', surface: 'app', page: 'SubscriptionCard', goal: 'upgrade',
    name: 'Ajustes · Empezar prueba', destination: 'stripe' },
  // La misma card para un comp en MODO CLOSING (Business regalado por ventas
  // mientras cierra): id propio para ver aparte cuántos de esos cierres pagan.
  { id: 'app.ajustes.closing', surface: 'app', page: 'SubscriptionCard', goal: 'upgrade',
    name: 'Ajustes · Contratar (modo closing)', destination: 'stripe' },
  // El enlace de pago que genera un admin para mandarlo por WhatsApp tras un
  // onboarding. No es un botón que pulse el cliente: el modal del panel lo
  // declara y el backend (`billing/payment_links.py`) lo pone en el checkout,
  // así las altas que entran por ahí salen aquí. Sin clicks ni impresiones.
  { id: 'app.admin.link-pago', surface: 'app', page: 'PaymentLinkModal', goal: 'upgrade',
    name: 'Enlace de pago de ventas (WhatsApp)', destination: 'stripe' },
  // La card de suscripción de la app, cuando el usuario tiene más fichas
  // activas de las que cubre Business. Se ve en todos los sitios donde se monta
  // `UpgradeCard`, por eso no lleva la pantalla en el id.
  { id: 'app.precios.enterprise-mas-30', surface: 'app', page: 'SubscriptionCard', goal: 'sales',
    name: 'Card de suscripción · Más de 30 locales → Enterprise', destination: 'chat' },
  { id: 'app.automatizaciones.autoreply', surface: 'app', page: 'Automations', goal: 'upgrade',
    name: 'Automatizaciones · Activar respuesta automática', destination: 'PricingModal' },
  { id: 'app.automatizaciones.alerta-keyword', surface: 'app', page: 'Automations', goal: 'upgrade',
    name: 'Automatizaciones · Activar alerta por palabra clave', destination: 'PricingModal' },
  { id: 'app.automatizaciones.alerta-valoracion', surface: 'app', page: 'Automations', goal: 'upgrade',
    name: 'Automatizaciones · Activar alerta por valoración', destination: 'PricingModal' },
  { id: 'app.publicaciones.desbloquear', surface: 'app', page: 'GMBPublications', goal: 'upgrade',
    name: 'Publicaciones · Ver planes', destination: 'PricingModal' },

  // Los muros que levanta INTENTAR usar la función, uno por acción. No son
  // botones de upsell —«Publicar», «Programar» y «Generar con IA» los pulsa
  // también quien ya paga—, así que NO van envueltos en `<CtaLink>`: el click lo
  // emite la página con `trackCtaClick` en el momento en que el muro se levanta,
  // que es la única vez que ese botón hace de CTA. Sin esto los cuatro caminos
  // abrían el modal sin id y el alta se la quedaba la pantalla de precios, con
  // lo que no había forma de saber cuál de las tres funciones vende.
  { id: 'app.publicaciones.publicar', surface: 'app', page: 'GMBPublications', goal: 'upgrade',
    name: 'Publicaciones · Intentar publicar', destination: 'PricingModal' },
  { id: 'app.publicaciones.programar', surface: 'app', page: 'GMBPublications', goal: 'upgrade',
    name: 'Publicaciones · Intentar programar', destination: 'PricingModal' },
  { id: 'app.publicaciones.ia', surface: 'app', page: 'GMBPublications', goal: 'upgrade',
    name: 'Publicaciones · Intentar generar con IA', destination: 'PricingModal' },
  // Los otros dos muros del asistente de publicaciones (las sugerencias del
  // paso 4 reutilizan `app.publicaciones.ia`).
  { id: 'app.publicaciones.foto', surface: 'app', page: 'PostWizard', goal: 'upgrade',
    name: 'Publicaciones · Intentar añadir foto', destination: 'PricingModal' },
  { id: 'app.publicaciones.ajuste-ia', surface: 'app', page: 'PostWizard', goal: 'upgrade',
    name: 'Publicaciones · Intentar ajustar el texto con IA', destination: 'PricingModal' },
  { id: 'app.perfil.editar', surface: 'app', page: 'GMBProfile', goal: 'upgrade',
    name: 'Perfil · Intentar editar la ficha', destination: 'PricingModal' },
  // El muro de la protección de ficha (§152): es una función de los locales
  // Pro, así que sin Pro la tarjeta entera va desenfocada tras este CTA.
  { id: 'app.perfil.proteccion', surface: 'app', page: 'GMBProfile', goal: 'upgrade',
    name: 'Perfil · Desbloquear protección automática', destination: 'PricingModal' },
  // El muro de las sugerencias de categorías con IA (§183), en la tarjeta de
  // categorías de la vista: un Free no llega nunca al editor.
  { id: 'app.perfil.categorias-ia', surface: 'app', page: 'GMBProfile', goal: 'upgrade',
    name: 'Perfil · Desbloquear sugerencias de categorías con IA', destination: 'PricingModal' },

  // La cápsula de la cabecera es un componente COMPARTIDO (`BusinessUpsellCta`),
  // montado en varias páginas. Hasta ahora las catorce compartían
  // `modal.upsell.abrir`, así que el ranking decía «la cápsula vende» y no desde
  // dónde. Estas dos reciben su id por prop; el resto sigue con el común, y por
  // eso `page` es la PÁGINA que las monta y no el componente: es lo que las
  // distingue.
  { id: 'app.publicaciones.capsula', surface: 'app', page: 'GMBPublications', goal: 'upgrade',
    name: 'Publicaciones · Cápsula de cabecera', destination: 'PricingModal' },
  { id: 'app.perfil.capsula', surface: 'app', page: 'GMBProfile', goal: 'upgrade',
    name: 'Perfil · Cápsula de cabecera', destination: 'PricingModal' },
  { id: 'app.health-score.seo', surface: 'app', page: 'GMBHealthScore', goal: 'upgrade',
    name: 'Salud de la ficha · Desbloquear SEO', destination: 'PricingModal' },
  { id: 'app.health-score-v2.seo', surface: 'app', page: 'GMBHealthScoreV2', goal: 'upgrade',
    name: 'SEO Score v2 · Desbloquear análisis IA', destination: 'PricingModal' },
  { id: 'app.metricas.conversion', surface: 'app', page: 'GMBMetrics', goal: 'upgrade',
    name: 'Métricas · Desbloquear conversión', destination: 'PricingModal' },
  { id: 'app.metricas.plataformas', surface: 'app', page: 'GMBMetrics', goal: 'upgrade',
    name: 'Métricas · Desbloquear reparto por plataforma', destination: 'PricingModal' },
  { id: 'app.metricas.busquedas', surface: 'app', page: 'GMBMetrics', goal: 'upgrade',
    name: 'Métricas · Desbloquear búsquedas', destination: 'PricingModal' },
  { id: 'app.metricas.analisis-ia', surface: 'app', page: 'GMBMetrics', goal: 'upgrade',
    name: 'Métricas · Desbloquear análisis con IA', destination: 'PricingModal' },
  { id: 'app.asiento.upgrade', surface: 'app', page: 'SeatActionPanel', goal: 'upgrade',
    name: 'Panel de Business · Pasar a Business', destination: '/pricing' },
  // El botón de cada local sin Business en la lista de Ajustes. Dice «Ampliar
  // plan» o «Pasar a Business» según queden asientos libres, pero es el mismo
  // botón: abre el modal del asiento para ESE local.
  { id: 'app.ajustes.ampliar-plan', surface: 'app', page: 'ConnectionList', goal: 'upgrade',
    name: 'Ajustes · Ampliar plan para un local', destination: 'Modal de asiento' },

  // Los cinco muros de página completa (`PreviewOverlay` + `ProGatePanel`). Uno
  // por página y no el `modal.preview.empezar-trial` compartido que había antes:
  // el desglose por pantalla ya existía para los CLICKS (cada evento guarda su
  // `path`), pero el ALTA DE PLAN va keyeada por id, así que con un id común no
  // había forma de saber qué función bloqueada era la que acababa convirtiendo.
  // Al separarlos, la serie del id viejo se corta el día del despliegue: sigue
  // visible en el panel con su histórico —la tabla une los ids CON EVENTOS a los
  // declarados— pero ya no suma.
  { id: 'app.informes.empezar-trial', surface: 'app', page: 'ReportsInformes', goal: 'upgrade',
    name: 'Muro de Informes · Empezar', destination: 'stripe' },
  { id: 'app.sentimiento.empezar-trial', surface: 'app', page: 'ReportsSentiment', goal: 'upgrade',
    name: 'Muro de Sentimiento · Empezar', destination: 'stripe' },
  { id: 'app.nps.empezar-trial', surface: 'app', page: 'ReportsNPS', goal: 'upgrade',
    name: 'Muro de NPS · Empezar', destination: 'stripe' },
  { id: 'app.keywords.empezar-trial', surface: 'app', page: 'ReportsKeywords', goal: 'upgrade',
    name: 'Muro de Palabras clave · Empezar', destination: 'stripe' },
  { id: 'app.integraciones.empezar-trial', surface: 'app', page: 'Integraciones', goal: 'upgrade',
    name: 'Muro de Integraciones · Empezar', destination: 'stripe' },

  // ── Onboarding ─────────────────────────────────────────────────────────
  { id: 'onboarding.stepper.continuar', surface: 'onboarding', page: 'OnboardingStepper', goal: 'nav',
    name: 'Recorrido inicial · Continuar', destination: 'paso siguiente' },
  // El banner «Último paso» que ocupa el hueco del stepper al terminar el
  // recorrido (fue la cápsula de cabecera tal cual hasta octubre de 2026; el id
  // se conserva para no partir el histórico). Id propio para saber si vende
  // aparte de la de cabecera.
  { id: 'onboarding.fin.business', surface: 'onboarding', page: 'OnboardingDoneUpsell', goal: 'upgrade',
    name: 'Recorrido terminado · Pasar a Business', destination: 'PricingModal' },

  // ── Retención (botones de CANCELAR — nunca en el ranking de conversión) ──
  { id: 'retention.ajustes.cancelar', surface: 'retention', page: 'GbusinessSettings', goal: 'retention',
    name: 'Ajustes · Cancelar suscripción', destination: 'CancelSubscriptionModal' },

  // ── Conexión de la ficha de Google ──────────────────────────────────────
  // Superficie propia y no `app`/`onboarding` porque los ocho son UN embudo, y
  // es el embudo que se quiere mirar entero: sin ficha conectada la app no
  // enseña nada, así que esto es la activación de verdad.
  //
  // Van separados por SITUACIÓN, no por componente: conectar por primera vez
  // (activación), reconectar tras un token muerto (rescate, §66) y reintentar
  // unos permisos que el usuario no marcó (`insufficient_scope`) tienen causas
  // y arreglos distintos. Con un id común se sumarían en una cifra que no dice
  // qué hacer.
  //
  // Los `connect` cuentan además la conexión COMPLETADA, que emite el servidor
  // en el callback del OAuth; los `nav` solo llevan a la pantalla desde la que
  // se conecta, así que solo cuentan clicks.
  { id: 'google.banner.conectar', surface: 'google', page: 'NoActiveLocationBanner', goal: 'connect',
    name: 'Banner · Conectar mi ficha de Google', destination: 'OAuth de Google' },
  { id: 'google.banner.reconectar', surface: 'google', page: 'NoActiveLocationBanner', goal: 'connect',
    name: 'Banner · Reconectar cuenta caducada', destination: 'OAuth de Google' },
  { id: 'google.banner.elegir-ficha', surface: 'google', page: 'NoActiveLocationBanner', goal: 'nav',
    name: 'Banner · Activar local', destination: '/SelectLocation' },
  { id: 'google.ajustes.conectar-otra', surface: 'google', page: 'ConnectionList', goal: 'connect',
    name: 'Cuentas · Conectar cuenta de Google', destination: 'OAuth de Google' },
  { id: 'google.ajustes.reconectar', surface: 'google', page: 'ConnectionList', goal: 'connect',
    name: 'Cuentas · Reconectar (token revocado)', destination: 'OAuth de Google' },
  { id: 'google.ajustes.permisos', surface: 'google', page: 'ConnectionList', goal: 'connect',
    name: 'Cuentas · Volver a conectar (faltan permisos)', destination: 'OAuth de Google' },
  { id: 'google.ficha.conectar', surface: 'google', page: 'LocationDetail', goal: 'nav',
    name: 'Ficha · Conectar mi ficha de Google', destination: '/GbusinessConnect' },
  { id: 'google.elegir-ficha.conectar-otra', surface: 'google', page: 'SelectLocation', goal: 'nav',
    name: 'Elegir local · Conectar otra cuenta', destination: '/GbusinessConnect' },
  // El modal «marca la casilla» que abren TODOS los de arriba antes de mandar a
  // Google (`GoogleConnectGuideDialog`). Su botón es `nav` a propósito: la
  // conexión se la lleva el botón que abrió el modal, y aquí solo se mide
  // cuánta gente llega hasta Google después de ver la casilla. El de
  // «reintentar» sí es `connect`: lo abre el callback al volver sin la casilla,
  // así que no hay otro origen al que atribuírsela.
  { id: 'google.guia.conectar', surface: 'google', page: 'GoogleConnectGuideDialog', goal: 'nav',
    name: 'Guía de permisos · Conectar con Google', destination: 'OAuth de Google' },
  { id: 'google.guia.reintentar', surface: 'google', page: 'GoogleConnectGuideDialog', goal: 'connect',
    name: 'Guía de permisos · Volver a conectar (falta la casilla)', destination: 'OAuth de Google' },
  // «Probar con otra cuenta» tras volver sin ninguna ficha (§174): borran la
  // cuenta vacía y abren Google con el selector. `connect` los dos: el del modal
  // lo abre el callback y el del banner es el origen del intento.
  { id: 'google.guia.otra-cuenta', surface: 'google', page: 'GoogleConnectGuideDialog', goal: 'connect',
    name: 'Guía · Probar con otra cuenta (sin ficha)', destination: 'OAuth de Google' },
  { id: 'google.ajustes.otra-cuenta', surface: 'google', page: 'ConnectionList', goal: 'connect',
    name: 'Cuentas · Probar con otra cuenta (sin ficha)', destination: 'OAuth de Google' },  // ── Plano Google Maps pt dentro de la app (`components/ofertaPt/anuncios/`) ──
  // «Ver o plano» de la tarjeta y de las franjas abre la ventana del plan; el
  // alta se la lleva ESE botón (viaja como origen al checkout), no el «Começar»
  // de la ventana, que se queda con el click. «Ligar com Google» es `connect`:
  // el id va también en la URL del OAuth.
  { id: 'ofertapt.dispositivos.ver-plano', surface: 'ofertapt', page: 'Devices', goal: 'upgrade',
    name: 'Plano Google Maps pt · Dispositivos · Ver o plano', destination: 'VentanaPlano' },
  { id: 'ofertapt.dispositivos.ligar-google', surface: 'ofertapt', page: 'Devices', goal: 'connect',
    name: 'Plano Google Maps pt · Dispositivos · Ligar com Google (sem GMB)', destination: 'OAuth de Google' },
  { id: 'ofertapt.franja.automatizacoes', surface: 'ofertapt', page: 'Automations', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Automatizações', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.avaliacoes', surface: 'ofertapt', page: 'Reviews', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Avaliações', destination: 'VentanaPlano' },
  // La tarjeta de debajo del composer de Avaliações, en lugar de la de la prueba
  // gratis (`FreeReplyQuotaCta`) mientras la oferta está activa.
  { id: 'ofertapt.avaliacoes.ver-plano', surface: 'ofertapt', page: 'Reviews', goal: 'upgrade',
    name: 'Plano Google Maps pt · Avaliações · Respostas grátis · Ver o plano', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.seo-local', surface: 'ofertapt', page: 'GMBHealthScore', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · SEO Local', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.mapa-seo', surface: 'ofertapt', page: 'GMBGeoGrid', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Mapa SEO', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.metricas', surface: 'ofertapt', page: 'GMBMetrics', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Métricas', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.empresa', surface: 'ofertapt', page: 'MyBusiness', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · A Minha Empresa', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.perfil', surface: 'ofertapt', page: 'GMBProfile', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Perfil do Negócio', destination: 'VentanaPlano' },
  { id: 'ofertapt.franja.publicacoes', surface: 'ofertapt', page: 'GMBPublications', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Publicações', destination: 'VentanaPlano' },
  // La misma tarjeta en Configurações (en lugar de la de suscripción), con sus ids.
  { id: 'ofertapt.configuracoes.ver-plano', surface: 'ofertapt', page: 'GbusinessSettings', goal: 'upgrade',
    name: 'Plano Google Maps pt · Configurações · Ver o plano', destination: 'VentanaPlano' },
  { id: 'ofertapt.configuracoes.ligar-google', surface: 'ofertapt', page: 'GbusinessSettings', goal: 'connect',
    name: 'Plano Google Maps pt · Configurações · Ligar com Google (sem GMB)', destination: 'OAuth de Google' },
  { id: 'ofertapt.franja.outras', surface: 'ofertapt', page: 'FranjaPlano', goal: 'upgrade',
    name: 'Plano Google Maps pt · Franja · Resto de páginas (informes, NPS…)', destination: 'VentanaPlano' },
  { id: 'ofertapt.ventana.comprar-topo', surface: 'ofertapt', page: 'VentanaPlano', goal: 'upgrade',
    name: 'Plano Google Maps pt · Ventana · Barra superior (ordenador)', destination: 'stripe' },
  { id: 'ofertapt.ventana.comprar-hero', surface: 'ofertapt', page: 'VentanaPlano', goal: 'upgrade',
    name: 'Plano Google Maps pt · Ventana · Hero', destination: 'stripe' },
  { id: 'ofertapt.ventana.comprar-preco', surface: 'ofertapt', page: 'VentanaPlano', goal: 'upgrade',
    name: 'Plano Google Maps pt · Ventana · Tarjeta de precio', destination: 'stripe' },
  { id: 'ofertapt.ventana.comprar-barra', surface: 'ofertapt', page: 'VentanaPlano', goal: 'upgrade',
    name: 'Plano Google Maps pt · Ventana · Barra fija inferior (móvil)', destination: 'stripe' },
  // No es un botón: la ventana que se abre sola un segundo después del login
  // (`useAbrirTrasLogin`). Sin clicks; se lleva las altas que salgan de ahí.
  { id: 'ofertapt.login.auto', surface: 'ofertapt', page: 'Devices', goal: 'upgrade',
    name: 'Plano Google Maps pt · Ventana automática tras el login', destination: 'VentanaPlano' },
  { id: 'ofertapt.ventana.whatsapp', surface: 'ofertapt', page: 'VentanaPlano', goal: 'sales',
    name: 'Plano Google Maps pt · Ventana · Fala com o Afaq (WhatsApp)', destination: 'whatsapp' },
];

export const CTA_IDS = new Set(CTA_REGISTRY.map((c) => c.id));

export const CTA_SURFACE_VALUES = CTA_SURFACES.map((s) => s.value);

/** Espejo de `INTERNAL_ID_RE` en `QR/qr_app/cta_registry.py`. */
export const CTA_ID_RE = /^[a-z0-9]+(\.[a-z0-9-]+){1,3}$/;

export const getCta = (id) => CTA_REGISTRY.find((c) => c.id === id) || null;

/** Etiqueta traducida de una superficie, o el valor crudo si falta la clave. */
export const surfaceLabel = (value, t) => {
  const found = CTA_SURFACES.find((s) => s.value === value);
  return found ? t(found.labelKey) : value || '';
};
